import NavBar from '@/components/NavBar';
import SideBar from '@/components/SideBar';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { ErrorBoundaryWithLocation } from '@/ErrorBoundaryWithLocation';
import { endSession, resetLogoutTimer } from '@/services/inactivityTimeOut';
import { isSessionStale, LAST_ACTIVITY_KEY } from '@/services/lastActivity';
import { useFilePackageStatusPolling } from '@/services/filePackageStatusPolling';
import { useUserStore } from '@/stores/user';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

const Template: React.FC = () => {
  const topRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const userStore = useUserStore((state) => state);
  const { setIsDirty } = useContext(NavigationBlockerContext);
  // Pages don't mount until the stored session has been checked, so none of
  // their requests (or token refreshes) can run for a session being ended.
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    // Reopened after the inactivity limit (the browser was closed): end that session.
    if (useUserStore.getState().accessToken && isSessionStale()) {
      void endSession('BROWSER_CLOSED', { showExpiredMessage: true });
      return;
    }
    resetLogoutTimer();
    setSessionChecked(true);
  }, []);

  useFilePackageStatusPolling();

  useEffect(() => {
    if (userStore.InactivityTimeout || !userStore.accessToken) navigate('/login');
  }, [userStore]);

  // Clear any stale unsaved-changes flag once navigation to a new page has
  // committed, so the sidebar confirmation alert does not leak into list views.
  useEffect(() => {
    setIsDirty(false);
  }, [location.pathname, setIsDirty]);

  useEffect(() => {
    if (location.pathname === '/') navigate('/dashboard', { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Every API response updates the shared last-activity time; that alone mustn't reload other tabs.
      if (event.key === LAST_ACTIVITY_KEY) return;
      window.location.reload();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <>
      <div className="flex flex-col h-screen overflow-hidden">
        <div className="z-20">
          <NavBar />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className={`z-10 hidden md:block`}>
            <SideBar />
          </div>
          <div
            className="z-0 mt-[75px] w-full flex min-w-0 flex-1 flex-col items-stretch md:ml-[250px] p-8 pb-14 overflow-auto scrollbar-hide"
            ref={topRef}
          >
            <ErrorBoundaryWithLocation>{sessionChecked ? <Outlet /> : null}</ErrorBoundaryWithLocation>
          </div>
        </div>
      </div>
    </>
  );
};

export default Template;
